import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { marked } from "marked";
import { api, API } from "@/lib/api";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import WhatsAppFloat from "@/components/site/WhatsAppFloat";
import { useSiteContent } from "@/pages/Conteudos";
import { Loader2 } from "lucide-react";

export default function ContentPage() {
  const { slug } = useParams();
  const content = useSiteContent();
  const [c, setC] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setC(null);
    setNotFound(false);
    api.get(`/public/contents/${slug}`)
      .then(({ data }) => setC(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!c) return;
    api.get("/public/contents").then(({ data }) => {
      const rel = data.filter((x) => x.id !== c.id);
      rel.sort((a, b) => ((b.keyword === c.keyword) - (a.keyword === c.keyword)));
      setRelated(rel.slice(0, 3));
    }).catch(() => {});
  }, [c]);

  const bodyHtml = useMemo(() => (c ? marked.parse(c.body || "") : ""), [c]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
        <Navbar content={content} />
        <main className="flex-1 grid place-items-center px-5">
          <div className="text-center">
            <h1 className="font-display text-4xl text-[#2C332A]">Conteúdo não encontrado</h1>
            <p className="font-body text-sm text-[#7A8A7A] mt-3">Este conteúdo pode ter sido despublicado.</p>
            <Link to="/" className="inline-flex items-center rounded-full bg-[#5B6E5B] px-6 py-3 mt-6 text-[#FDFBF7] font-body text-sm hover:bg-[#4A5D4E] transition-colors">
              Voltar ao início
            </Link>
          </div>
        </main>
        <Footer content={content} />
      </div>
    );
  }

  if (!c) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FDFBF7]">
        <Loader2 className="w-7 h-7 animate-spin text-[#5B6E5B]" data-testid="content-loading" />
      </div>
    );
  }

  const canonical = c.type === "artigo"
    ? `${window.location.origin}/conteudos/${c.slug}`
    : `${window.location.origin}/${c.slug}`;
  const title = c.meta_title || c.title;
  const desc = c.meta_description || c.excerpt || "";

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col" data-testid="content-page">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content={c.type === "artigo" ? "article" : "website"} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={canonical} />
        {c.cover_image && <meta property="og:image" content={`${API}/files/${c.cover_image}`} />}
      </Helmet>
      <Navbar content={content} />
      <main className="flex-1 max-w-3xl mx-auto w-full px-5 pt-32 pb-20">
        <p className="font-body text-xs tracking-[0.35em] uppercase text-[#C87A5E]">
          {c.type === "artigo" ? "Artigo" : "Atendimento online"}
        </p>
        <h1 className="font-display text-4xl sm:text-5xl text-[#2C332A] mt-3 leading-tight" data-testid="content-title">{c.title}</h1>
        {c.excerpt && <p className="font-body text-base sm:text-lg text-[#4A5D4E] mt-4 leading-relaxed">{c.excerpt}</p>}
        {c.cover_image && (
          <img src={`${API}/files/${c.cover_image}`} alt={c.title} className="w-full rounded-2xl mt-8" data-testid="content-cover" />
        )}

        <article
          className="mt-8 font-body text-[#2C332A] leading-relaxed
            [&_h2]:font-display [&_h2]:text-3xl [&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:leading-snug
            [&_h3]:font-display [&_h3]:text-2xl [&_h3]:mt-8 [&_h3]:mb-2
            [&_p]:mb-4 [&_ul]:mb-4 [&_ul]:pl-6 [&_ul]:list-disc [&_ol]:mb-4 [&_ol]:pl-6 [&_ol]:list-decimal
            [&_li]:mb-1.5 [&_strong]:text-[#4A5D4E] [&_a]:text-[#5B6E5B] [&_a]:underline
            [&_blockquote]:border-l-[3px] [&_blockquote]:border-[#C87A5E] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-[#4A5D4E] [&_blockquote]:mb-4"
          data-testid="content-body"
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />

        {(c.faq || []).length > 0 && (
          <section className="mt-12" data-testid="content-faq">
            <h2 className="font-display text-3xl text-[#2C332A] mb-4">Perguntas frequentes</h2>
            <div className="space-y-3">
              {c.faq.map((f, i) => (
                <details key={i} className="rounded-2xl bg-white border border-[#5B6E5B]/10 px-5 py-4 group">
                  <summary className="font-body font-semibold text-[#2C332A] cursor-pointer list-none flex items-center justify-between gap-3">
                    {f.q}
                    <span className="text-[#5B6E5B] transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <p className="font-body text-sm text-[#4A5D4E] mt-3 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        <div className="mt-12 rounded-3xl bg-[#F5F0E6] p-8 text-center" data-testid="content-cta">
          <p className="font-display text-2xl text-[#2C332A]">{c.cta || "Converse comigo sobre o atendimento psicológico online"}</p>
          <p className="font-body text-sm text-[#7A8A7A] mt-2">Psicoterapia online, com sigilo e acolhimento, para adultos em todo o Brasil.</p>
          <a href="/#contato" data-testid="content-cta-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#5B6E5B] px-7 py-3.5 mt-5 text-[#FDFBF7] font-body text-sm transition-colors hover:bg-[#4A5D4E]">
            Agendar uma conversa
          </a>
        </div>

        {related.length > 0 && (
          <section className="mt-12" data-testid="content-related">
            <h2 className="font-display text-2xl text-[#2C332A] mb-4">Conteúdos relacionados</h2>
            <div className="space-y-3">
              {related.map((r) => (
                <Link key={r.id} to={r.type === "artigo" ? `/conteudos/${r.slug}` : `/${r.slug}`}
                  className="block rounded-2xl bg-white border border-[#5B6E5B]/10 px-5 py-4 transition-colors hover:border-[#5B6E5B]/30">
                  <p className="font-body font-semibold text-[#2C332A]">{r.title}</p>
                  {r.excerpt && <p className="font-body text-sm text-[#7A8A7A] mt-1">{r.excerpt}</p>}
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer content={content} />
      <WhatsAppFloat content={content} />
    </div>
  );
}
